/**
 * Post Shared Preview Tab Utils
 *
 * Shared utilities for preview tab structure, rich preview hosts, and SNS/OGP sections.
 */

import { OgpPreviewHandler } from "../../util/ogp/ogp-preview-handler";
import { translator } from "../../translation/translator";
import { PostSharedPreviewContentUtils } from "./post-shared-preview-utils";
import { PostSharedPreviewRender } from "./post-shared-preview-render";
import { CommonUtils } from "../../util/common-utils";
import { BoardId } from "../../constants/board-id";
import { UISymbols } from "../../constants/ui-symbols";
import { postSharedPreviewForm } from "./post-shared-preview-form";

const HtmlInsertPosition = {
    BEFORE_BEGIN: "beforebegin" as InsertPosition,
    AFTER_BEGIN: "afterbegin" as InsertPosition,
};

interface EnsureRichHostResult {
    previewBody: HTMLElement;
    richHost: HTMLElement;
}

interface RenderPreviewTitleParams {
    formRoot: HTMLElement;
    previewBody: HTMLElement;
}

interface RenderRichTextParams {
    richHost: HTMLElement;
    html: string;
    placeholderText?: string;
}

interface UpdateOgpPreviewInTabParams {
    contentTextarea: HTMLTextAreaElement;
    previewTabContainer: HTMLElement;
}

interface AfterRichRenderContext {
    richHost: HTMLElement;
    previewBody: HTMLElement;
    cleanHtml: string;
}

interface UpdatePreviewTabParams {
    formRoot: HTMLElement;
    previewRoot: HTMLElement;
    html: string;
    snsUrls?: string[];
    placeholderText?: string;
    afterRichRender?: (ctx: AfterRichRenderContext) => void;
}

interface SyncContentCharCounterPreviewParams {
    formRoot: HTMLElement;
    previewRoot: HTMLElement;
}

interface RenderReviewQuestionsPreviewParams {
    formRoot: HTMLElement;
    previewBody: HTMLElement;
    richHost?: HTMLElement;
}

class PostSharedPreviewTabUtils {
    public static getPlaceholderText(fallback = "เนื้อหาจะแสดงที่นี่..."): string {
        try {
            if (translator && typeof translator.getTranslation === "function") {
                return translator.getTranslation("content_placeholder") || fallback;
            }
        } catch (_error) {
            void _error;
        }
        return fallback;
    }

    public static ensureRichHost(previewRoot: HTMLElement): EnsureRichHostResult {
        const previewBodyCandidate = postSharedPreviewForm.getPreviewBody(previewRoot) || previewRoot;
        const previewBody = previewBodyCandidate instanceof HTMLElement ? previewBodyCandidate : previewRoot;
        const richHost = PostSharedPreviewRender.ensureRichHost(previewRoot);

        return {
            previewBody,
            richHost,
        };
    }

    public static renderPreviewTitle(params: RenderPreviewTitleParams): void {
        const { formRoot, previewBody } = params;

        const boardId = String(formRoot.getAttribute("data-board-id") || "").trim();
        if (boardId === BoardId.REVIEW) {
            const existingTitle = postSharedPreviewForm.getPreviewTitle(previewBody);
            if (existingTitle) {
                existingTitle.remove();
            }
            return;
        }

        const titleInput = postSharedPreviewForm.getPreviewTitleField(formRoot);

        const titleValue = titleInput?.value ? titleInput.value.trim() : "";

        PostSharedPreviewRender.renderPreviewTitle({ previewBody, titleValue });
    }

    public static renderRichText(params: RenderRichTextParams): void {
        const { richHost, html, placeholderText } = params;

        if (html && String(html).trim()) {
            richHost.innerHTML = String(html);
            richHost.removeAttribute("data-placeholder");
            return;
        }

        const placeholder = placeholderText || PostSharedPreviewTabUtils.getPlaceholderText();
        richHost.setAttribute("data-placeholder", placeholder);
        richHost.innerHTML = "";
    }

    public static updateOgpPreviewInTab(params: UpdateOgpPreviewInTabParams): void {
        const { contentTextarea, previewTabContainer } = params;

        const content = contentTextarea.value || "";
        const ogpPreview = new OgpPreviewHandler();
        const urls = ogpPreview.ogpRender ? ogpPreview.ogpRender.detectUrls(content) : [];

        if (urls.length === 0) {
            previewTabContainer.innerHTML = "";
            const section = postSharedPreviewForm.getOgpPreviewSection(previewTabContainer);
            if (section instanceof HTMLElement) {
                section.style.display = "none";
            }
            return;
        }

        ogpPreview.updatePreviewWithUrls(urls, previewTabContainer);
        const section = postSharedPreviewForm.getOgpPreviewSection(previewTabContainer);
        if (section instanceof HTMLElement) {
            section.style.display = "block";
        }
    }

    public static updatePreviewTab(params: UpdatePreviewTabParams): EnsureRichHostResult {
        const { formRoot, previewRoot, html, snsUrls, placeholderText, afterRichRender } = params;

        const { previewBody, richHost } = PostSharedPreviewTabUtils.ensureRichHost(previewRoot);

        PostSharedPreviewTabUtils.renderPreviewTitle({ formRoot, previewBody });

        const cleanHtml = html
            ? PostSharedPreviewContentUtils.buildCleanPreviewHtml({
                  html,
                  snsUrls: Array.isArray(snsUrls) ? snsUrls : [],
              })
            : "";

        PostSharedPreviewTabUtils.renderRichText({ richHost, html: cleanHtml, placeholderText });

        if (cleanHtml && typeof afterRichRender === "function") {
            try {
                afterRichRender({ richHost, previewBody, cleanHtml });
            } catch (_error) {
                void _error;
            }
        }

        PostSharedPreviewRender.renderAttachmentsPreviewGrid({ previewRoot, formRoot });
        PostSharedPreviewTabUtils.syncContentCharCounterPreview({ formRoot, previewRoot });

        return { previewBody, richHost };
    }

    public static syncContentCharCounterPreview(params: SyncContentCharCounterPreviewParams): void {
        const { formRoot, previewRoot } = params;

        const sourceCounter = postSharedPreviewForm.getContentCounter(formRoot);
        const previewCounter = postSharedPreviewForm.getPreviewContentCounter(previewRoot);
        if (!previewCounter) return;

        if (!(sourceCounter instanceof HTMLElement)) {
            previewCounter.dataset.count = "0";
            previewCounter.style.setProperty("--post-content-progress", "0%");
            const previewCount = postSharedPreviewForm.getContentCountElement(previewCounter);
            if (previewCount instanceof HTMLElement) {
                previewCount.textContent = "0";
            }
            const fill = postSharedPreviewForm.getContentProgressFill(previewCounter);
            if (fill instanceof HTMLElement) {
                fill.style.width = "0%";
                fill.classList.remove("low", "middle", "high");
                fill.classList.add("low");
            }
            ["100", "201"].forEach((threshold) => {
                const badge = postSharedPreviewForm.getContentThresholdBadge(previewCounter, threshold);
                if (badge instanceof HTMLElement) {
                    badge.classList.remove("is-reached");
                }
            });
            return;
        }

        const sourceCount = postSharedPreviewForm.getContentCountElement(sourceCounter);
        const previewCount = postSharedPreviewForm.getContentCountElement(previewCounter);
        if (sourceCount instanceof HTMLElement && previewCount instanceof HTMLElement) {
            previewCount.textContent = sourceCount.textContent || "0";
        }

        const sourceProgress = sourceCounter.style.getPropertyValue("--post-content-progress") || "0%";
        previewCounter.style.setProperty("--post-content-progress", sourceProgress || "0%");
        previewCounter.dataset.count = sourceCounter.dataset.count || "0";

        const sourceFill = postSharedPreviewForm.getContentProgressFill(sourceCounter);
        const previewFill = postSharedPreviewForm.getContentProgressFill(previewCounter);
        if (previewFill instanceof HTMLElement) {
            const width = sourceFill instanceof HTMLElement ? sourceFill.style.width : sourceProgress;
            previewFill.style.width = width || sourceProgress || "0%";
            previewFill.classList.remove("low", "middle", "high");
            ["low", "middle", "high"].forEach((stateClass) => {
                if (sourceFill instanceof HTMLElement && sourceFill.classList.contains(stateClass)) {
                    previewFill.classList.add(stateClass);
                }
            });
            if (
                !previewFill.classList.contains("low") &&
                !previewFill.classList.contains("middle") &&
                !previewFill.classList.contains("high")
            ) {
                previewFill.classList.add("low");
            }
        }

        ["100", "201"].forEach((threshold) => {
            const sourceBadge = postSharedPreviewForm.getContentThresholdBadge(sourceCounter, threshold);
            const previewBadge = postSharedPreviewForm.getContentThresholdBadge(previewCounter, threshold);
            if (previewBadge instanceof HTMLElement) {
                const reached =
                    sourceBadge instanceof HTMLElement ? sourceBadge.classList.contains("is-reached") : false;
                previewBadge.classList.toggle("is-reached", reached);
            }
        });
    }

    public static renderReviewQuestionsPreview(params: RenderReviewQuestionsPreviewParams): void {
        const { formRoot, previewBody, richHost } = params;

        const existing = postSharedPreviewForm.getReviewQuestionsPreview(previewBody);
        if (existing) {
            existing.remove();
        }

        const questions = postSharedPreviewForm.getReviewQuestionElements(formRoot);
        if (!questions || questions.length === 0) {
            return;
        }

        const rowsHtml: string[] = [];
        const ratingValues: number[] = [];

        const parseMultiValues = (raw: string): string[] => {
            const value = String(raw || "").trim();
            if (!value) return [];

            if (value.startsWith("[")) {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                        return parsed.map((item) => String(item).trim()).filter(Boolean);
                    }
                } catch (_error) {
                    void _error;
                }
            }

            if (value.includes(",")) {
                return value
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean);
            }

            return [value];
        };

        const getSelectLabelByValue = (selectEl: HTMLSelectElement | null, value: string): string => {
            if (!(selectEl instanceof HTMLSelectElement)) return "";
            const strValue = String(value ?? "");
            for (const option of Array.from(selectEl.options)) {
                if (String(option.value) === strValue) {
                    return String(option.textContent || "").trim();
                }
            }
            return "";
        };

        questions.forEach((questionElement) => {
            if (!(questionElement instanceof HTMLElement)) return;

            const labelEl = postSharedPreviewForm.getReviewQuestionLabel(questionElement);
            const questionText = (
                labelEl?.textContent ||
                questionElement.getAttribute("data-question-th") ||
                ""
            ).trim();
            const questionType = String(questionElement.getAttribute("data-question-type") || "").trim();

            let answerText = "-";
            let pickerAnswers: string[] = [];
            let ratingValue: number | null = null;

            const ratingHidden = postSharedPreviewForm.getRatingAnswerInput(questionElement);
            if (ratingHidden) {
                const raw = String(ratingHidden.value || "").trim();
                const value = parseInt(raw, 10);
                if (Number.isFinite(value) && value > 0) {
                    ratingValue = Math.max(0, Math.min(5, value));
                    ratingValues.push(ratingValue);
                }
            }

            const checkedRadio = postSharedPreviewForm.getCheckedReviewRadio(questionElement);
            if (checkedRadio) {
                const optionLabel = postSharedPreviewForm.getReviewOptionLabel(checkedRadio);
                const optionTextEl = postSharedPreviewForm.getReviewOptionText(optionLabel || questionElement);
                const text = (optionTextEl?.textContent || "").trim();
                answerText = text || answerText;
                if (questionType === "single_select" && text) {
                    pickerAnswers = [text];
                }
            }

            if (questionType === "single_select" && pickerAnswers.length === 0) {
                const singleHidden = postSharedPreviewForm.getSingleSelectAnswerInput(questionElement);
                const selectedValue = String(singleHidden?.value || "").trim();
                if (selectedValue) {
                    const optionTextFromSpan = postSharedPreviewForm.getSingleSelectOptionText(
                        questionElement,
                        selectedValue,
                    );
                    const optionTextFromSelect = getSelectLabelByValue(
                        postSharedPreviewForm.getSingleSelectElement(questionElement),
                        selectedValue,
                    );
                    const selectedText = (optionTextFromSpan?.textContent || optionTextFromSelect || "").trim();
                    if (selectedText) {
                        pickerAnswers = [selectedText];
                        answerText = selectedText;
                    }
                }
            }

            if (questionType === "multi_select") {
                const multiHidden = postSharedPreviewForm.getMultiSelectAnswerInput(questionElement);
                const selectEl = postSharedPreviewForm.getMultiSelectElement(questionElement);

                const rawValues = parseMultiValues(multiHidden?.value || "");
                const selectedValues =
                    rawValues.length > 0
                        ? rawValues
                        : Array.from(selectEl?.selectedOptions || [])
                              .map((option) => String(option?.value || "").trim())
                              .filter(Boolean);

                const labels = selectedValues.map((value) => getSelectLabelByValue(selectEl, value)).filter(Boolean);

                if (labels.length > 0) {
                    pickerAnswers = labels;
                    answerText = labels.join(", ");
                }
            }

            const textArea = postSharedPreviewForm.getReviewAnswerTextarea(questionElement);
            if (textArea) {
                const text = String(textArea.value || "").trim();
                answerText = text || "-";
            }

            let answerHtml = "";
            if (typeof ratingValue === "number" && ratingValue > 0) {
                const starsHtml = Array.from({ length: 5 }, (_, index) => {
                    const starIndex = index + 1;
                    const klass = starIndex <= ratingValue ? "star-preview-icon--filled" : "star-preview-icon--empty";
                    return `<i class="star-preview-icon ${klass}">${UISymbols.STAR_FILLED}</i>`;
                }).join("");
                answerHtml = `<div class="rating-stars">${starsHtml}<span class="rating-value">(${ratingValue})</span></div>`;
            } else if (pickerAnswers.length > 0) {
                if (questionType === "multi_select") {
                    answerHtml = `<p class="answer-text option-text">${CommonUtils.escapeHtml(pickerAnswers.join(", "))}</p>`;
                } else {
                    answerHtml = `<span class="review-answer-picker">${CommonUtils.escapeHtml(pickerAnswers[0] || "")}</span>`;
                }
            } else if (answerText !== "-") {
                if (questionType === "multi_select") {
                    answerHtml = `<p class="answer-text option-text">${CommonUtils.escapeHtml(answerText)}</p>`;
                } else {
                    answerHtml = `<span class="review-answer-picker">${CommonUtils.escapeHtml(answerText)}</span>`;
                }
            }

            rowsHtml.push(`
                <div class="review-answer-item">
                    <div class="review-question">
                        <i class="bi bi-question-circle" aria-hidden="true"></i>
                        <strong class="question-text">${CommonUtils.escapeHtml(questionText)}</strong>
                    </div>
                    <div class="review-answer">${answerHtml}</div>
                </div>
            `);
        });

        const ratingCount = ratingValues.length;
        const ratingAverage = ratingCount > 0 ? ratingValues.reduce((sum, value) => sum + value, 0) / ratingCount : 0;
        const averageStarsHtml =
            ratingCount > 0
                ? Array.from({ length: 5 }, (_, index) => {
                      const starIndex = index + 1;
                      const klass =
                          starIndex <= Math.floor(ratingAverage)
                              ? "star-preview-icon--filled"
                              : starIndex === Math.floor(ratingAverage) + 1 && ratingAverage % 1 >= 0.5
                                ? "star-preview-icon--half"
                                : "star-preview-icon--empty";
                      return `<i class="star-preview-icon ${klass}">${UISymbols.STAR_FILLED}</i>`;
                  }).join("")
                : "";
        const averageText = ratingCount > 0 ? `(${CommonUtils.formatAverageRating(ratingAverage, 2, "-")})` : "";
        const previewHtml = PostSharedPreviewRender.buildReviewQuestionsPreviewHtml({
            rowsHtml,
            ratingCount,
            averageStarsHtml,
            averageText,
        });

        const richHostNode = richHost instanceof HTMLElement ? richHost : null;
        const previewCounter = postSharedPreviewForm.getPreviewContentCounter(previewBody);
        if (previewCounter) {
            previewCounter.insertAdjacentHTML(HtmlInsertPosition.BEFORE_BEGIN, previewHtml);
            return;
        }

        if (richHostNode && richHostNode.parentNode === previewBody) {
            richHostNode.insertAdjacentHTML(HtmlInsertPosition.BEFORE_BEGIN, previewHtml);
            return;
        }

        previewBody.insertAdjacentHTML(HtmlInsertPosition.AFTER_BEGIN, previewHtml);
    }
}

export { PostSharedPreviewTabUtils };
